import { loadImageAsync } from '@/utils/file-kit';

// cspell:ignore aliyuncs
const VIDEO_FIRST_FRAME_SEEK_TIME = 0.1;
const VIDEO_FIRST_FRAME_TIMEOUT = 10_000;

/**
 * 获取视频封面 URL
 *
 * 根据存储服务类型拼接视频首帧截图参数，并避免重复处理已有封面参数。
 *
 * @param url - 可选；视频 URL
 * @returns 视频封面 URL；视频 URL 为空时返回空字符串
 */
export function getVideoCoverUrl(url?: string): string {
    if (!url) {
        return '';
    }
    if (url.includes('vframe/')) {
        return url;
    }
    const queryParam = 'vframe/jpg/offset/0';
    if (url.includes('imageView2/') || url.includes('.aliyuncs.com')) {
        url = url.split('?')[0];
    }
    if (url.includes('.aliyuncs.com')) {
        return `${url}?x-oss-process=video/snapshot,t_1000,f_jpg`;
    }
    return url.includes('?') ? `${url}&${queryParam}` : `${url}?${queryParam}`;
}

/**
 * 通过浏览器解码视频并截取第一帧
 *
 * 使用 0.1 秒画面规避部分 Safari 无法绘制 0 秒帧的问题。
 *
 * @param videoUrl - 视频 URL
 * @param timeout - 加载超时时间，单位毫秒
 * @returns 视频第一帧 Canvas
 */
export function loadVideoFirstFrame(videoUrl: string, timeout = VIDEO_FIRST_FRAME_TIMEOUT): Promise<HTMLCanvasElement> {
    return new Promise((resolve, reject) => {
        const video = document.createElement('video');
        let settled = false;

        const cleanup = () => {
            clearTimeout(timeoutId);
            video.removeEventListener('canplay', handleCanPlay);
            video.removeEventListener('seeked', handleSeeked);
            video.removeEventListener('error', handleError);
            try {
                video.pause();
                video.removeAttribute('src');
                video.load();
            } catch {
                // 清理失败不影响封面生成结果
            }
            video.remove();
        };
        const fail = (error: Error) => {
            if (settled) return;
            settled = true;
            cleanup();
            reject(error);
        };
        const drawFrame = () => {
            if (settled) return;
            if (!video.videoWidth || !video.videoHeight) {
                fail(new Error('video frame has invalid dimensions'));
                return;
            }

            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const context = canvas.getContext('2d');
            if (!context) {
                fail(new Error('video frame canvas context is unavailable'));
                return;
            }

            try {
                context.drawImage(video, 0, 0, canvas.width, canvas.height);
                settled = true;
                cleanup();
                resolve(canvas);
            } catch (error) {
                fail(error instanceof Error ? error : new Error('video frame drawing failed'));
            }
        };
        const handleCanPlay = () => {
            const seekTime =
                Number.isFinite(video.duration) && video.duration > 0
                    ? Math.min(VIDEO_FIRST_FRAME_SEEK_TIME, video.duration / 2)
                    : VIDEO_FIRST_FRAME_SEEK_TIME;
            try {
                if (Math.abs(video.currentTime - seekTime) < Number.EPSILON) {
                    drawFrame();
                    return;
                }
                video.currentTime = seekTime;
            } catch {
                drawFrame();
            }
        };
        const handleSeeked = () => drawFrame();
        const handleError = () => fail(new Error('video first frame loading failed'));
        const timeoutId = setTimeout(() => fail(new Error('video first frame loading timed out')), timeout);

        video.crossOrigin = 'anonymous';
        video.preload = 'auto';
        video.muted = true;
        video.playsInline = true;
        video.src = videoUrl;
        video.addEventListener('canplay', handleCanPlay);
        video.addEventListener('seeked', handleSeeked);
        video.addEventListener('error', handleError);
        video.load();
    });
}

/**
 * 按优先级加载视频封面
 *
 * 依次尝试显式封面、URL 首帧截图参数，最后通过浏览器解码视频截取第一帧。
 *
 * @param posterUrl - 可选；显式封面 URL
 * @param videoUrl - 视频 URL
 * @param loadFirstFrame - 视频解码截帧函数
 * @returns 可用于渲染的图片或 Canvas
 */
export async function loadVideoCover(
    posterUrl: string | undefined,
    videoUrl: string,
    loadFirstFrame: (url: string) => Promise<HTMLCanvasElement> = loadVideoFirstFrame,
): Promise<HTMLImageElement | HTMLCanvasElement> {
    const imageCandidates = [...new Set([posterUrl, getVideoCoverUrl(videoUrl)])].filter((url): url is string =>
        Boolean(url),
    );

    for (const imageUrl of imageCandidates) {
        try {
            return await loadImageAsync(imageUrl);
        } catch {
            // 当前候选不可用时继续尝试下一级封面来源
        }
    }

    if (!videoUrl) throw new Error('video URL is empty');
    return loadFirstFrame(videoUrl);
}

/**
 * 解析媒体图层应使用的封面 URL
 *
 * 显式传入的封面优先；否则默认尝试加载视频首帧截图，加载失败时回退到视频 URL。
 *
 * @param videoUrl - 视频 URL
 * @param coverUrl - 可选；调用方显式指定的封面 URL
 * @param autoGeneratePoster - 是否自动生成首帧封面
 * @returns 新封面 URL；关闭自动生成且未传封面时返回 undefined
 */
export async function resolveVideoPosterUrl(
    videoUrl: string,
    coverUrl?: string,
    autoGeneratePoster = true,
): Promise<string | undefined> {
    if (coverUrl !== undefined) {
        await loadImageAsync(coverUrl).catch(() => undefined);
        return coverUrl;
    }
    if (!autoGeneratePoster) return undefined;

    const generatedPosterUrl = getVideoCoverUrl(videoUrl);
    try {
        await loadImageAsync(generatedPosterUrl);
        return generatedPosterUrl;
    } catch {
        return videoUrl;
    }
}

/** 加载视频获取元信息 */
export function loadVideoMetaInfo(
    videoUrl: string,
    maxRetries = 3,
): Promise<{ width: number; height: number; duration: number }> {
    return new Promise((resolve, reject) => {
        let attempt = 0;

        const tryLoad = () => {
            attempt++;
            // 创建视频元素
            const video = document.createElement('video');

            // 设置跨域（如果视频来自其他域名）
            video.crossOrigin = 'anonymous';

            // 视频元数据加载完成时触发
            video.addEventListener('loadedmetadata', () => {
                const safeDuration =
                    Number.isFinite(video.duration) && video.duration > 0 ? Math.round(video.duration) : 0;

                // 获取宽高
                const dimensions = {
                    width: video.videoWidth,
                    height: video.videoHeight,
                    duration: safeDuration,
                };
                resolve(dimensions);

                // 销毁视频元素（可选，释放资源）
                video.remove();
            });

            // 加载失败处理
            video.addEventListener('error', (_err) => {
                video.remove();
                if (attempt < maxRetries) {
                    tryLoad();
                } else {
                    reject(new Error('load video metadata failed'));
                }
            });

            // 设置视频源并开始加载元数据
            video.src = videoUrl;
            video.load(); // 触发加载
        };

        tryLoad();
    });
}
